import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowUpRight, Sparkles, Crown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Occasion() {
  const { user } = useAuth();
  const [occasions, setOccasions] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/occasions").then((r) => setOccasions(r.data.occasions || []));
    if (user) {
      api.get("/occasion-analyses").then((r) => setRecent((r.data.analyses || []).slice(0, 3)));
    }
  }, [user]);

  return (
    <main className="crown-container py-12 md:py-16" data-testid="occasion-page">
      {/* Hero */}
      <section className="grid lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-7 animate-fade-up">
          <p className="font-overline mb-4">The Atelier of Ceremony</p>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[0.95] mb-6">
            For the days you'll<br/>
            <span className="italic text-gold">remember forever.</span>
          </h1>
          <p className="text-ash leading-relaxed mb-8 max-w-xl">
            Upload a selfie — and an optional full-body shot — and our atelier will
            curate a complete look: dress silhouette, hair, accessories, and a
            photoreal try-on of the entire moment.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/occasion/new" className="crown-btn-primary" data-testid="occasion-start-cta">
              Begin a ceremony reading <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
            {!user?.is_premium && (
              <Link to="/pricing" className="crown-btn-ghost">
                <Crown size={14} strokeWidth={1.5} /> Unlock unlimited
              </Link>
            )}
          </div>
          {!user?.is_premium && (
            <p className="font-overline text-ash mt-6">1 free ceremony reading included</p>
          )}
        </div>
        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
          <img src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&q=80" alt="" className="aspect-[3/4] object-cover" />
          <img src="https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=800&q=80" alt="" className="aspect-[3/4] object-cover mt-12" />
        </div>
      </section>

      {/* Occasions grid */}
      <section className="mb-20" data-testid="occasions-grid">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-overline">Pick the moment</p>
            <h2 className="font-serif text-3xl mt-2">Six ceremonies. One atelier.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {occasions.map((o) => (
            <Link
              key={o.id}
              to={`/occasion/new?occasion=${o.id}`}
              className="group relative aspect-[4/5] overflow-hidden"
              data-testid={`occasion-card-${o.id}`}
            >
              <img src={o.image_url} alt={o.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-espresso/95 via-espresso/30 to-transparent" />
              <div className="absolute top-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowUpRight className="text-gold" strokeWidth={1.5} size={20} />
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-overline text-gold text-[0.65rem]">{o.subtitle}</p>
                <p className="font-serif text-2xl mt-1 leading-tight">{o.name}</p>
                <p className="text-xs text-ash mt-2 italic">{o.vibe}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Recent readings */}
      {user && recent.length > 0 && (
        <section data-testid="recent-occasion-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="font-overline flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> Your archive</p>
              <h2 className="font-serif text-3xl mt-2">Recent ceremony readings.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {recent.map((a) => (
              <Link key={a.id} to={`/occasion/${a.id}`} className="block crown-card group" data-testid={`recent-occ-${a.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={a.occasion_meta?.image_url} alt={a.occasion_name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <p className="font-overline text-ash text-[0.65rem]">{new Date(a.created_at).toLocaleDateString()}</p>
                  <p className="font-serif text-xl mt-2">{a.occasion_name}</p>
                  <p className="text-sm text-ash mt-2 line-clamp-2">{a.result?.styling_notes}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
